import type { ReactNode } from 'react';
import authForge from '../assets/auth/tosho-login-forge.webp';
import toshoLogo from '../assets/tosho-logo.svg';
import { TOSHO_PRODUCT_VERSION } from '../product-version';

interface AuthShellProps {
  readonly children?: ReactNode;
  readonly title: string;
  readonly intro?: string;
  readonly eyebrow?: string;
  readonly footer?: ReactNode;
}

/**
 * Chrome d'authentification public Tosho.
 *
 * Aucun branding d'organisation n'est affiché tant que le contexte d'organisation n'est pas connu
 * de manière fiable. Cette séparation évite de figer KATANA dans le produit et prépare l'usage
 * multi-tenant sans sous-domaine.
 *
 * Les assets visuels applicatifs sont importés depuis `src/assets`. Vite garantit ainsi leur
 * présence dans le build final, leur versionnement avec le bundle et leur URL générée. Aucun chemin
 * public absolu ni data URI n'est codé à la main pour ces assets.
 */
export function AuthShell({
  children,
  title,
  intro,
  eyebrow = 'Plateforme privée',
  footer,
}: AuthShellProps): React.JSX.Element {
  return (
    <main className="l-auth-shell">
      <section className="auth-panel" aria-labelledby="auth-title">
        <header className="auth-brand">
          <div className="auth-brand__tosho" aria-label="Tosho">
            <img className="auth-brand__logo" src={toshoLogo} alt="Tosho" />
          </div>
        </header>

        <div className="auth-panel__body">
          <p className="auth-panel__eyebrow">{eyebrow}</p>
          <h1 id="auth-title">{title}</h1>
          {intro ? <p className="auth-panel__intro">{intro}</p> : null}
          {children}
        </div>

        <footer className="auth-panel__footer">
          {footer ?? <span>{TOSHO_PRODUCT_VERSION}</span>}
        </footer>
      </section>
      <div className="auth-visual" aria-hidden="true">
        <img className="auth-visual__image" src={authForge} alt="" />
        <div className="auth-visual__veil" />
      </div>
    </main>
  );
}
